import type { BuildingTypeId } from '../sim/buildings/definitions';
import type { ScreenPoint } from '../core/types';

/** What produced a pointer action. Touch has no hover, so some flows differ (e.g. build confirm). */
export type PointerKind = 'mouse' | 'touch' | 'pen';

/**
 * Device-independent game actions. Mouse, keyboard, buttons and touch
 * gestures all translate into these; gameplay code never reads raw pointer
 * events. This is what lets mobile reuse the same game logic.
 */
export type GameAction =
  /** Primary "tap/click" on the world at a screen point (select / inspect / place). */
  | { type: 'primary'; screen: ScreenPoint; pointer: PointerKind }
  /** Two-finger gesture step: zoom by `factor` about `from`, then move that point to `to` (immediate, no smoothing). */
  | { type: 'pinch'; factor: number; from: ScreenPoint; to: ScreenPoint }
  /** Touch drag that started on the placement preview: move the preview to this point. */
  | { type: 'placementDrag'; screen: ScreenPoint }
  /** Build the previewed building (touch confirm button / Enter). */
  | { type: 'confirmBuild' }
  /** Pointer moved without a press (hover preview). Optional on touch. */
  | { type: 'hover'; screen: ScreenPoint | null }
  /** Pan the camera by a screen-space delta. */
  | { type: 'pan'; dx: number; dy: number }
  /** Multiplicative zoom about a screen point (null = viewport centre). */
  | { type: 'zoom'; factor: number; screen: ScreenPoint | null }
  /** Secondary click (right button today; a touch "cancel" control later). Cancels build mode. */
  | { type: 'secondary'; screen: ScreenPoint }
  | { type: 'resetView' }
  | { type: 'clearSelection' }
  /** Back out of the current mode: leave build mode, otherwise clear the selection. */
  | { type: 'cancel' }
  /** Enter (or toggle off) placement mode for a building type. */
  | { type: 'selectBuild'; building: BuildingTypeId }
  /** Construction-bar keyboard shortcut ("1".."5"), resolved to a building by the scene. */
  | { type: 'buildHotkey'; key: string }
  | { type: 'demolish'; id: number }
  /** Box selection drag (Box mode on, or Shift + drag with a mouse). `done` on release. */
  | { type: 'boxSelect'; from: ScreenPoint; to: ScreenPoint; done: boolean }
  /** Toggle Box mode (one-finger / left drags select instead of panning). */
  | { type: 'toggleBoxMode' }
  /** Select every unit the local player owns. */
  | { type: 'selectArmy' }
  /** Selected units stop. */
  | { type: 'stopUnits' }
  /** Clear the unit selection. */
  | { type: 'deselectUnits' }
  /** Arm / disarm attack-move: the next tap / click on the map sends the selected units there, fighting on the way. */
  | { type: 'toggleAttackMove' }
  /** Selected units' stance; `toggle` flips Aggressive ⇄ Hold (keyboard H). */
  | { type: 'setStance'; stance: import('../sim/units/unitDefs').Stance | 'toggle' }
  /** Queue / cancel training at a building. */
  | { type: 'trainUnit'; buildingId: number; unit: import('../sim/units/unitDefs').UnitTypeId }
  | { type: 'cancelTraining'; buildingId: number }
  /** Open / close the colony status panel. */
  | { type: 'toggleColony' }
  /** Save this colony as your raid base (Phase 13). */
  | { type: 'saveBase' }
  /** Start a new match (result screen). */
  | { type: 'playAgain' }
  | { type: 'togglePause' }
  | { type: 'cycleSpeed' }
  | { type: 'startBriefing' }
  /** Start a building's upgrade (Phase 8). */
  | { type: 'upgradeBuilding'; id: number }
  | { type: 'lureHorde'; id: number; target: string }
  | { type: 'startResearch'; research: string }
  /** Change the number of workers at a workplace by ±1. */
  | { type: 'adjustWorkers'; id: number; delta: number }
  | { type: 'toggleGrid' }
  | { type: 'togglePassability' }
  | { type: 'toggleDebug' };

export type ActionListener = (action: GameAction) => void;

/** Minimal synchronous event bus for actions. */
export class ActionBus {
  private readonly listeners = new Set<ActionListener>();

  on(listener: ActionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(action: GameAction): void {
    for (const l of this.listeners) l(action);
  }

  /** Number of subscribers; exposed for duplicate-handler diagnostics. */
  get listenerCount(): number {
    return this.listeners.size;
  }

  clear(): void {
    this.listeners.clear();
  }
}
